import { Button } from '@/components/ui/button';
import { useCallback } from 'react';

/**
 * S5 - the confirmation before a closure (chat spec §11).
 *
 * **Signed at the gesture**, at the régime `admin/experience-gesture-confirm`
 * holds for the irreversible acts of the catalogue, and §11 names that régime
 * on purpose. What is borrowed is the SHAPE and the reason for it:
 *
 * - it is asked at the moment of the gesture and never before, so nothing is
 *   dark on a screen somebody just arrived on;
 * - it NAMES what is about to happen instead of asking « êtes-vous sûr » - the
 *   effect on the guest, the read-only that follows, the fact that it cannot be
 *   taken back;
 * - a native `<dialog open>` kept in flow by React state, under a white veil,
 *   because the ground of this application is white. Escape cancels: `open`
 *   alone does not wire it, unlike `showModal()`.
 *
 * **What is NOT borrowed is the identity field**, and that is a decision rather
 * than an omission. « Qui enregistre » exists over there because every catalogue
 * gesture writes a journal line that has to be signed; a closure writes no
 * journal in this lot, so a name field would collect something nothing reads -
 * and §6 is explicit that no identity is collected on this side of the product.
 * If a room journal ever lands, this is where the field belongs.
 *
 * It renders for the INTERVIEWER only, because it is only ever mounted from a
 * control that does not exist in the guest's DOM (§3). The refusal that makes
 * that true is on the server, in `routes/chat-close.ts`: the role comes from
 * the resolved token, so this component is the courtesy and not the guard.
 */

export interface ChatCloseConfirmProps {
  /** True while the POST is in flight, so the gesture cannot be fired twice. */
  closing: boolean;
  /**
   * The refusal to show, worded by the caller from the status. Null when
   * nothing has failed - a live region that is created at the same moment it
   * fills is not read out, so the caller keeps it mounted.
   */
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ChatCloseConfirm({ closing, error, onConfirm, onCancel }: ChatCloseConfirmProps) {
  // Focus moves into the dialog on mount, onto CANCEL and not onto the
  // destructive button: a stray Enter must not end an interview. A `useCallback`
  // ref rather than `autoFocus` (the a11y lint refuses it) and rather than an
  // effect - a stable callback runs on attach and on nothing else.
  const focusOnMount = useCallback((node: HTMLButtonElement | null) => node?.focus(), []);

  return (
    <div
      data-testid="chat-close-confirm"
      className="fixed inset-0 z-40 flex items-center justify-center bg-white/80 p-5"
    >
      <dialog
        open
        aria-label="Terminer l’entretien ?"
        onKeyDown={(event) => {
          if (event.key === 'Escape') onCancel();
        }}
        className="relative m-0 w-full max-w-lg space-y-4 rounded-md border-2 border-gray-300 bg-white p-6 shadow-lg"
      >
        <h2 className="font-semibold text-bleu-nuit text-lg">Terminer l’entretien ?</h2>
        <p className="text-gray-700 text-sm">
          L’invité verra un écran de fin. La conversation passe en lecture seule pour vous deux et
          ne peut pas être reprise.
        </p>
        <p className="text-gray-700 text-sm">
          Un message que votre interlocuteur a déjà validé peut encore arriver après la clôture. Il
          sera conservé, et signalé comme arrivé après.
        </p>

        {/* Mounted from the first render, empty or not: see `error` above. */}
        <p aria-live="polite" className="text-rouge-deep text-sm">
          {error}
        </p>

        <div className="flex flex-wrap justify-end gap-3">
          <Button ref={focusOnMount} type="button" variant="outline" onClick={onCancel}>
            Annuler
          </Button>
          <Button type="button" variant="destructive" disabled={closing} onClick={onConfirm}>
            {closing ? 'Clôture...' : 'Terminer l’entretien'}
          </Button>
        </div>
      </dialog>
    </div>
  );
}
